import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Supercluster from "supercluster";
import { supabase } from "../lib/supabase";
import { ensureOperatorsSuccess } from "../lib/operatorsApi";
import { useSearchStore } from "../stores/searchStore";
import { useAuthStore } from "../stores/authStore";
import type { OperatorWithCoords, EventOccurrence } from "../types/database";
import { getCacheData, setCacheData, generateCacheKey, getTotalOperatorsCount } from "../lib/operatorsCache";

// GeoJSON Feature type for operators
export interface OperatorFeature {
  type: "Feature";
  properties: OperatorWithCoords & { cluster?: false };
  geometry: {
    type: "Point";
    coordinates: [number, number]; // [lng, lat]
  };
}

// Cluster feature from Supercluster
export interface ClusterFeature {
  type: "Feature";
  properties: {
    cluster: true;
    cluster_id: number;
    point_count: number;
    point_count_abbreviated: string | number;
    // Typology tracking: if all points have same typology, this is that typology; if mixed, it's null
    uniformTypology?: string | null;
    // Macro category tracking: if all points have same macro_category, this is that category; if mixed, it's null
    uniformMacroCategory?: string | null;
  };
  geometry: {
    type: "Point";
    coordinates: [number, number]; // [lng, lat]
  };
}

export type MapFeature = OperatorFeature | ClusterFeature;

// Check if feature is a cluster
export function isCluster(feature: MapFeature): feature is ClusterFeature {
  return feature.properties.cluster === true;
}

// Custom cluster properties for typology and category tracking (only custom fields - Supercluster adds cluster/cluster_id/point_count automatically)
export interface CustomClusterProps {
  // Typology tracking: if all points have same typology, this is that typology; if mixed, it's null
  uniformTypology: string | null;
  // Macro category tracking: if all points have same macro_category, this is that category; if mixed, it's null
  uniformMacroCategory: string | null;
}

// Full cluster properties (built-in + custom)
export interface ClusterProperties extends CustomClusterProps {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: string | number;
}

// Supercluster options with map/reduce to track typology in clusters
// Optimized for large datasets (86k+ operators)
const SUPERCLUSTER_OPTIONS: Supercluster.Options<
  OperatorWithCoords,
  CustomClusterProps
> = {
  radius: 100, // Slightly larger clusters to reduce markers rendered
  maxZoom: 18, // Keep clustering even at very high zoom levels
  minZoom: 0,
  minPoints: 2,
  extent: 512,
  nodeSize: 96, // Fewer tree levels, faster getClusters on dense data
  // Map each point to extract its typology and macro category
  map: (props) => ({
    uniformTypology: props.typology || null,
    uniformMacroCategory: props.macro_category || null,
  }),
  // Reduce: combine typologies - if all same keep it, if different set to null (mixed)
  reduce: (accumulated, props) => {
    // Typology tracking
    if (accumulated.uniformTypology !== null) {
      if (accumulated.uniformTypology !== props.uniformTypology) {
        accumulated.uniformTypology = null;
      }
    }
    // Macro category tracking
    if (accumulated.uniformMacroCategory !== null) {
      if (accumulated.uniformMacroCategory !== props.uniformMacroCategory) {
        accumulated.uniformMacroCategory = null;
      }
    }
  },
};

// Page size for progressive loading (loads operators in batches)
// Batch size: 5000 è un compromesso — valori più grandi (30k) fanno scadere il
// CPU timeout di Deno su Supabase edge-runtime self-hosted quando il dataset è
// > 30k record. 5k evita il timeout e tiene comunque basso il numero di roundtrip
// (~23 chiamate per 113k record).
const PAGE_SIZE = 20000;

// Cache key prefix
const CACHE_KEY_PREFIX = "all-operators";

/**
 * Protected API call to operators-query Edge Function.
 * Retry con exponential backoff su 500/503/timeout: il Deno edge-runtime
 * saltuariamente esaurisce il CPU quota durante il paging massivo e rigetta
 * il batch; un retry dopo 300-1200ms riesce quasi sempre.
 *
 * Se `signal` viene abortito, la chain di retry si ferma immediatamente
 * (senza aspettare il backoff in corso) — evita accumulo di fetch "zombie"
 * quando l'utente cambia filtri più volte di seguito.
 */
async function queryOperatorsProtected(
  token: string,
  action: string,
  params: Record<string, unknown> = {},
  signal?: AbortSignal,
  attempt = 0
): Promise<OperatorWithCoords[]> {
  if (signal?.aborted) throw new DOMException("aborted", "AbortError");
  const MAX_ATTEMPTS = 3;
  try {
    const { data, error } = await supabase.functions.invoke("operators-query", {
      body: { action, params },
      headers: { "X-Portale-Token": token },
    });
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    return ensureOperatorsSuccess<OperatorWithCoords[]>(data, error);
  } catch (err) {
    if (signal?.aborted) throw err;
    if (attempt >= MAX_ATTEMPTS - 1) throw err;
    const msg = err instanceof Error ? err.message : String(err);
    const isRetryable = msg.includes("non-2xx")
      || msg.includes("500")
      || msg.includes("503")
      || msg.includes("Failed to fetch")
      || msg.includes("timeout");
    if (!isRetryable) throw err;
    const backoffMs = 300 * Math.pow(2, attempt);
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, backoffMs);
      const onAbort = () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      };
      if (signal) {
        if (signal.aborted) { clearTimeout(t); reject(new DOMException("aborted", "AbortError")); return; }
        signal.addEventListener("abort", onAbort, { once: true });
      }
    });
    return queryOperatorsProtected(token, action, params, signal, attempt + 1);
  }
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** Sort occurrences chronologically (ISO date strings; null dates last). */
function compareByStartDate(a: EventOccurrence, b: EventOccurrence): number {
  const sa = a.start_date ?? "";
  const sb = b.start_date ?? "";
  if (sa === sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;
  return sa < sb ? -1 : 1;
}

/**
 * Convert operators to GeoJSON features for Supercluster.
 *
 * Operators sharing the same event identity (name + exact coordinates) are
 * collapsed into a SINGLE feature that carries all its date occurrences. This
 * turns the many Ticketone rows of the same event (one per date) into one pin,
 * while different events on the same point remain separate features.
 */
function operatorsToFeatures(operators: OperatorWithCoords[]): OperatorFeature[] {
  const groups = new Map<string, OperatorWithCoords[]>();
  for (const op of operators) {
    const key = `${op.name} ${op.lat} ${op.lng}`;
    const group = groups.get(key);
    if (group) group.push(op);
    else groups.set(key, [op]);
  }

  const features: OperatorFeature[] = [];
  for (const group of groups.values()) {
    const rep = group[0];

    if (group.length === 1) {
      features.push({
        type: "Feature",
        properties: { ...rep, cluster: false },
        geometry: { type: "Point", coordinates: [rep.lng, rep.lat] },
      });
      continue;
    }

    // Same event repeated on multiple dates -> one pin with all occurrences.
    const occurrences: EventOccurrence[] = group
      .map((o) => ({ id: o.id, start_date: o.start_date, end_date: o.end_date }))
      .sort(compareByStartDate);
    const starts = group.map((o) => o.start_date).filter((d): d is string => !!d);
    const ends = group.map((o) => o.end_date).filter((d): d is string => !!d);

    features.push({
      type: "Feature",
      properties: {
        ...rep,
        // Span the whole period so the collapsed pin still has coherent dates.
        start_date: starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : rep.start_date,
        end_date: ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : rep.end_date,
        occurrences,
        cluster: false,
      },
      geometry: { type: "Point", coordinates: [rep.lng, rep.lat] },
    });
  }

  return features;
}

/**
 * One representative per event (name + exact coordinates), carrying all its
 * date occurrences. Reuses operatorsToFeatures' grouping so the Visible/Filtered
 * counts and lists match exactly the one-pin-per-event shown on the map.
 */
export function aggregateByEvent(operators: OperatorWithCoords[]): OperatorWithCoords[] {
  return operatorsToFeatures(operators).map((f) => f.properties as OperatorWithCoords);
}

/**
 * Hook to get clusters for the current map viewport
 * Uses Supercluster for efficient clustering of large datasets
 * PROTECTED by authentication - loads progressively with caching
 */
export function useSupercluster() {
  const queryClient = useQueryClient();
  const { filters, mapBounds, zoomLevel, drawnPolygon, listMode, setResults, setTotalInView, setFilteredCount } = useSearchStore();
  const { token, isAuthenticated } = useAuthStore();

  // State for progressive loading
  const [allOperators, setAllOperators] = useState<OperatorWithCoords[]>([]);
  // Unfiltered operators for counting (loaded once without filters)
  const [unfilteredOperators, setUnfilteredOperators] = useState<OperatorWithCoords[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Stable reference for operatorsForCounting to prevent unnecessary recalculations
  const operatorsForCountingRef = useRef<OperatorWithCoords[]>([]);
  const lastCountingDepsRef = useRef<string>('');

  // Load unfiltered operators once for counting purposes
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setUnfilteredOperators([]);
      return;
    }

    const controller = new AbortController();

    async function loadUnfiltered() {
      // Check if we already have unfiltered operators
      const unfilteredCacheKey = [CACHE_KEY_PREFIX, "all", "all", "none", "none"];
      const memoryCache = queryClient.getQueryData<OperatorWithCoords[]>(unfilteredCacheKey);
      if (memoryCache && memoryCache.length > 0) {
        setUnfilteredOperators(memoryCache);
        return;
      }

      // Load all operators without filters
      try {
        let page = 0;
        let hasMore = true;
        let accumulated: OperatorWithCoords[] = [];

        while (hasMore) {
          if (controller.signal.aborted) return;
          const from = page * PAGE_SIZE;
          const queryParams = {
            offset: from,
            limit: PAGE_SIZE,
            categories: null,
            types: null,
            region: null,
            dateStart: null,
            dateEnd: null,
          };
          const data = await queryOperatorsProtected(token!, "search_all", queryParams, controller.signal);

          if (!data || data.length === 0) {
            hasMore = false;
            break;
          }

          const transformed = data.map((op) => ({
            ...op,
            lat: Number((op as { lat?: number; latitude?: number }).lat ?? (op as { latitude?: number }).latitude),
            lng: Number((op as { lng?: number; longitude?: number }).lng ?? (op as { longitude?: number }).longitude),
          })) as OperatorWithCoords[];

          accumulated = [...accumulated, ...transformed];

          if (data.length < PAGE_SIZE) {
            hasMore = false;
          }

          page++;
        }

        if (accumulated.length > 0 && !controller.signal.aborted) {
          setUnfilteredOperators(accumulated);
          queryClient.setQueryData(unfilteredCacheKey, accumulated);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("Error loading unfiltered operators:", err);
      }
    }

    loadUnfiltered();
    return () => controller.abort();
  }, [token, isAuthenticated, queryClient]);

  // Track current fetch to avoid duplicates
  const fetchIdRef = useRef(0);
  const clusterRef = useRef<Supercluster<OperatorWithCoords, Supercluster.AnyProps> | null>(null);

  // Create cache key for this filter combination
  // Use stable serialization to prevent unnecessary recalculations
  const cacheKey = useMemo(() => {
    const normalize = (values?: string[]) => {
      if (!values || values.length === 0) return "all";
      return [...values].sort().join("|");
    };
    const categoriesKey = normalize(filters.categories);
    const typesKey = normalize(filters.types);
    const dateStartKey = filters.dateRange?.start?.toISOString() || "none";
    const dateEndKey = filters.dateRange?.end?.toISOString() || "none";
    return [
      CACHE_KEY_PREFIX,
      categoriesKey,
      typesKey,
      dateStartKey,
      dateEndKey,
    ];
  }, [
    filters.categories ? [...filters.categories].sort().join("|") : "all",
    filters.types ? [...filters.types].sort().join("|") : "all",
    filters.dateRange?.start?.toISOString() || "none",
    filters.dateRange?.end?.toISOString() || "none",
  ]);

  // Generate persistent cache key with stable dependencies
  const persistentCacheKey = useMemo(() => generateCacheKey({
    categories: filters.categories,
    types: filters.types,
    region: null, // Region filter removed
    dateStart: filters.dateRange?.start?.toISOString().split("T")[0],
    dateEnd: filters.dateRange?.end?.toISOString().split("T")[0],
  }), [
    filters.categories ? [...filters.categories].sort().join("|") : null,
    filters.types ? [...filters.types].sort().join("|") : null,
    filters.dateRange?.start?.toISOString().split("T")[0] || null,
    filters.dateRange?.end?.toISOString().split("T")[0] || null,
  ]);

  // Progressive fetch with persistent caching (IndexedDB)
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setAllOperators([]);
      return;
    }

    const fetchId = ++fetchIdRef.current;
    const controller = new AbortController();

    // Clear operators immediately when filters change to prevent showing stale data
    console.log(`[useSupercluster] 🔄 Filters changed, clearing operators. New filters:`, {
      categories: filters.categories,
      types: filters.types,
      cacheKey: cacheKey.join('|'),
    });

    setAllOperators([]);

    async function loadProgressively() {
      setIsLoading(true);
      setIsError(false);

      // 1. Check in-memory cache first (React Query)
      const memoryCache = queryClient.getQueryData<OperatorWithCoords[]>(cacheKey);
      if (memoryCache && memoryCache.length > 0) {
        console.log(`[useSupercluster] ✅ Memory cache hit: ${memoryCache.length} operators`);
        // Only set if this fetch is still current
        if (fetchId === fetchIdRef.current) {
          setAllOperators(memoryCache);
        }
        setIsLoading(false);
        return;
      }

      // 2. Check persistent cache (IndexedDB) with count validation
      try {
        console.log(`[useSupercluster] Checking cache with key: ${persistentCacheKey}`);

        // Get current total count for cache validation
        const currentTotalCount = await getTotalOperatorsCount(token!);
        console.log(`[useSupercluster] Current total count from DB: ${currentTotalCount}`);

        const persistentCache = await getCacheData<OperatorWithCoords[]>(
          persistentCacheKey,
          currentTotalCount || undefined
        );

        if (persistentCache && persistentCache.length > 0) {
          console.log(`[useSupercluster] ✅ IndexedDB cache hit: ${persistentCache.length} operators (key: ${persistentCacheKey})`);
          // Only set if this fetch is still current
          if (fetchId === fetchIdRef.current) {
            setAllOperators(persistentCache);
            // Also update memory cache
            queryClient.setQueryData(cacheKey, persistentCache);
          }
          setIsLoading(false);
          return;
        } else {
          console.log(`[useSupercluster] ❌ IndexedDB cache miss or invalidated for key: ${persistentCacheKey}`);
        }
      } catch (e) {
        console.warn('[useSupercluster] IndexedDB check failed:', e);
      }

      // 3. No cache - fetch from API
      console.log(`[useSupercluster] 📡 Fetching from API...`, {
        categories: filters.categories,
        types: filters.types,
      });
      // Clear operators immediately when filters change to avoid showing stale data
      setAllOperators([]);

      let page = 0;
      let hasMore = true;
      let accumulated: OperatorWithCoords[] = [];

      try {
        while (hasMore) {
          if (fetchId !== fetchIdRef.current || controller.signal.aborted) return;

          const from = page * PAGE_SIZE;
          const queryParams = {
            offset: from,
            limit: PAGE_SIZE,
            categories: filters.categories || null,
            types: filters.types || null,
            region: null, // Region filter removed
            dateStart: filters.dateRange?.start?.toISOString().split("T")[0] || null,
            dateEnd: filters.dateRange?.end?.toISOString().split("T")[0] || null,
          };
          console.log(`[useSupercluster] Query params:`, queryParams);
          const data = await queryOperatorsProtected(token!, "search_all", queryParams, controller.signal);

          if (!data || data.length === 0) {
            hasMore = false;
            break;
          }

          const transformed = data.map((op) => ({
            ...op,
            lat: Number((op as { lat?: number; latitude?: number }).lat ?? (op as { latitude?: number }).latitude),
            lng: Number((op as { lng?: number; longitude?: number }).lng ?? (op as { longitude?: number }).longitude),
          })) as OperatorWithCoords[];

          accumulated = [...accumulated, ...transformed];

          if (fetchId === fetchIdRef.current && !controller.signal.aborted) {
            setAllOperators([...accumulated]);
          }

          if (data.length < PAGE_SIZE) {
            hasMore = false;
          }

          page++;
        }

        // Save to both caches when complete
        if (fetchId === fetchIdRef.current && !controller.signal.aborted && accumulated.length > 0) {
          // Memory cache
          queryClient.setQueryData(cacheKey, accumulated, {
            updatedAt: Date.now(),
          });

          // Get total count for cache invalidation
          const totalCount = await getTotalOperatorsCount(token!).catch(() => null);

          // Persistent cache (IndexedDB) - non-blocking, with total count for invalidation
          setCacheData(persistentCacheKey, accumulated, totalCount || undefined).catch((e) => {
            console.warn('[useSupercluster] Failed to save to IndexedDB:', e);
          });

          console.log(`[useSupercluster] 💾 Cached ${accumulated.length} operators (memory + IndexedDB)${totalCount ? ` with total count: ${totalCount}` : ''}`);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("Error loading operators:", err);
        if (fetchId === fetchIdRef.current) {
          setIsError(true);
        }
      } finally {
        if (fetchId === fetchIdRef.current && !controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    loadProgressively();
    return () => controller.abort();
    // `filters` non è in deps: i campi rilevanti (categories, types, dateRange)
    // sono già catturati da cacheKey/persistentCacheKey via serializzazione stabile.
    // Metterci l'oggetto `filters` intero causava re-fetch ad ogni setDrawnPolygon/
    // setMapBounds (che sostituiscono filters per un side-effect legacy), con
    // conseguente abort del paging e allOperators=[] → filtro per forma vuoto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAuthenticated, cacheKey, persistentCacheKey, queryClient]);

  // Build Supercluster index when operators change
  const supercluster = useMemo(() => {
    if (allOperators.length === 0) {
      clusterRef.current = null;
      return null;
    }

    const features = operatorsToFeatures(allOperators);
    const cluster = new Supercluster(SUPERCLUSTER_OPTIONS);
    cluster.load(features);
    clusterRef.current = cluster;

    return cluster;
  }, [allOperators]);

  // Filter operators by geographic search
  // If selectedZone is set (from autocomplete), do exact match only on city/region
  // Otherwise, if searchText is set (free text), search also in address
  const filteredBySearch = useMemo(() => {
    const selectedZone = filters.selectedZone;
    const searchText = filters.searchText?.trim();

    if (!searchText || allOperators.length === 0) {
      return allOperators;
    }

    const searchLower = searchText.toLowerCase();

    // If a zone was selected from autocomplete, do exact match only
    if (selectedZone) {
      const filtered = allOperators.filter((op) => {
        const city = op.city?.toLowerCase() || "";
        const region = op.region?.toLowerCase() || "";
        const name = op.name?.toLowerCase() || "";
        const address = op.address?.toLowerCase() || "";

        // Check if operator is a DMO
        const isDMO = op.macro_category === 'DMO' ||
          (op.typology && ['DMO', 'APT', 'ATL', 'OGD', 'DMC', 'Consorzio', 'Convention Bureau'].includes(op.typology));

        if (selectedZone.type === "city") {
          // Match operators with exact city match
          if (city === searchLower) {
            return true;
          }
          // Also include DMO that have city name in name/address but city field is empty
          // Must match region to ensure correct association
          // For address: city name must appear as actual city name, not just as part of street name
          if (isDMO && (!city || city === '') && region && selectedZone.region) {
            const zoneRegion = selectedZone.region.toLowerCase();
            if (region === zoneRegion) {
              // Match in name (any occurrence is fine)
              if (name.includes(searchLower)) {
                return true;
              }
              // Match in address: city name must appear as actual city name
              // Pattern: city name followed by parentheses with province code (e.g., "Roma (RM)")
              // OR city name followed by comma and postal code (e.g., "Roma, 00100")
              // OR city name at the end of address after comma
              if (address) {
                const addressLower = address.toLowerCase();
                // Pattern: "Roma (RM)" or "Roma (RM)," - city name followed by parentheses with province
                if (new RegExp(`\\b${searchLower}\\s*\\([A-Z]{2}\\)`).test(addressLower)) {
                  return true;
                }
                // Pattern: "Roma, 00100" - city name followed by comma and postal code
                if (new RegExp(`\\b${searchLower},\\s*\\d{5}`).test(addressLower)) {
                  return true;
                }
                // Pattern: city name at end of address (after last comma) - handles cases like "..., Roma"
                if (new RegExp(`,${searchLower}\\b`).test(addressLower)) {
                  return true;
                }
              }
            }
          }
          return false;
        } else if (selectedZone.type === "region") {
          return region === searchLower;
        }
        // For other zone types, fall back to exact match
        return city === searchLower || region === searchLower;
      });

      return filtered;
    }

    // Free text search: search in city, region, and address
    return allOperators.filter((op) => {
      const city = op.city?.toLowerCase() || "";
      const region = op.region?.toLowerCase() || "";
      const address = op.address?.toLowerCase() || "";
      // Exact match for city or region, or partial match in address
      return city === searchLower ||
        region === searchLower ||
        address.includes(searchLower);
    });
  }, [allOperators, filters.searchText, filters.selectedZone]);

  // Filter operators by drawn polygon (client-side)
  const filteredByPolygon = useMemo(() => {
    if (!drawnPolygon || filteredBySearch.length === 0) {
      return filteredBySearch;
    }
    return filteredBySearch.filter((op) => isPointInPolygon([op.lng, op.lat], drawnPolygon));
  }, [filteredBySearch, drawnPolygon]);

  // filteredOperators: operators with all filters applied (categories/types from server, polygon/search from client)
  // Note: allOperators already includes category/type filters applied server-side
  const filteredOperators = useMemo(() => {
    // Collapse to one entry per event (name + coords) so "Filtered" counts and
    // lists match the one-pin-per-event shown on the map.
    return aggregateByEvent(filteredByPolygon);
  }, [filteredByPolygon]);

  // All operators collapsed to one entry per event (for the overall total).
  const aggregatedAll = useMemo(() => aggregateByEvent(allOperators), [allOperators]);

  // Operators for counting: unfiltered by category/type, but filtered by geographic filters (search/polygon)
  // This ensures counts reflect geographic filters but not category/type selections
  // Use useMemo with stable dependencies and ref to prevent unnecessary recalculations
  const operatorsForCounting = useMemo(() => {
    // Create stable dependency string
    const depsKey = [
      unfilteredOperators.length,
      allOperators.length,
      filters.searchText || '',
      filters.selectedZone?.name || '',
      filters.selectedZone?.type || '',
      filters.selectedZone?.region || '',
      drawnPolygon ? JSON.stringify(drawnPolygon.coordinates?.[0]?.map(c => `${c[0]},${c[1]}`).join('|')) : '',
    ].join('|');

    // Return cached result if dependencies haven't changed
    if (depsKey === lastCountingDepsRef.current && operatorsForCountingRef.current.length > 0) {
      return operatorsForCountingRef.current;
    }

    // Start with unfiltered operators (no category/type filters)
    let base = unfilteredOperators.length > 0 ? unfilteredOperators : allOperators;

    // Apply geographic filters (search and polygon) if present
    const selectedZone = filters.selectedZone;
    const searchText = filters.searchText?.trim();

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      if (selectedZone) {
        // Exact match for selected zone
        base = base.filter((op) => {
          const city = op.city?.toLowerCase() || "";
          const region = op.region?.toLowerCase() || "";
          const name = op.name?.toLowerCase() || "";
          const address = op.address?.toLowerCase() || "";
          const isDMO = op.macro_category === 'DMO' ||
            (op.typology && ['DMO', 'APT', 'ATL', 'OGD', 'DMC', 'Consorzio', 'Convention Bureau'].includes(op.typology));

          if (selectedZone.type === "city") {
            if (city === searchLower) return true;
            if (isDMO && (!city || city === '') && region && selectedZone.region) {
              const zoneRegion = selectedZone.region.toLowerCase();
              if (region === zoneRegion) {
                if (name.includes(searchLower)) return true;
                if (address) {
                  const addressLower = address.toLowerCase();
                  if (new RegExp(`\\b${searchLower}\\s*\\([A-Z]{2}\\)`).test(addressLower)) return true;
                  if (new RegExp(`\\b${searchLower},\\s*\\d{5}`).test(addressLower)) return true;
                  if (new RegExp(`,${searchLower}\\b`).test(addressLower)) return true;
                }
              }
            }
            return false;
          } else if (selectedZone.type === "region") {
            return region === searchLower;
          }
          return city === searchLower || region === searchLower;
        });
      } else {
        // Free text search
        base = base.filter((op) => {
          const city = op.city?.toLowerCase() || "";
          const region = op.region?.toLowerCase() || "";
          const address = op.address?.toLowerCase() || "";
          return city === searchLower ||
            region === searchLower ||
            address.includes(searchLower);
        });
      }
    }

    // Apply polygon filter if present
    if (drawnPolygon && base.length > 0) {
      base = base.filter((op) => isPointInPolygon([op.lng, op.lat], drawnPolygon));
    }

    // Cache result
    operatorsForCountingRef.current = base;
    lastCountingDepsRef.current = depsKey;

    return base;
  }, [
    unfilteredOperators.length, // Use length instead of array reference
    allOperators.length, // Use length instead of array reference
    filters.searchText,
    filters.selectedZone?.name, // Use stable properties instead of object reference
    filters.selectedZone?.type,
    filters.selectedZone?.region,
    drawnPolygon ? JSON.stringify(drawnPolygon.coordinates?.[0]?.map(c => `${c[0]},${c[1]}`).join('|')) : null, // Use stable serialization
  ]);

  // If we have filters (polygon or search), create a separate Supercluster
  const filteredSupercluster = useMemo(() => {
    const hasFilters = drawnPolygon || filters.searchText;
    if (!hasFilters || filteredByPolygon.length === 0) {
      return null;
    }
    const features = operatorsToFeatures(filteredByPolygon);
    const cluster = new Supercluster(SUPERCLUSTER_OPTIONS);
    cluster.load(features);
    return cluster;
  }, [drawnPolygon, filters.searchText, filteredByPolygon]);

  // Get clusters for current viewport
  // Performance optimization: limit max markers to prevent freezing
  const MAX_MARKERS = 2500; // Maximum markers to render at once

  const clusters = useMemo((): MapFeature[] => {
    const hasFilters = drawnPolygon || filters.searchText;
    const activeCluster = hasFilters ? filteredSupercluster : supercluster;

    if (!activeCluster || !mapBounds) {
      return [];
    }

    const bbox: [number, number, number, number] = [
      mapBounds.west,
      mapBounds.south,
      mapBounds.east,
      mapBounds.north,
    ];

    try {
      // Use a slightly lower zoom level for clustering to reduce individual points
      // This helps performance when zooming in on dense areas
      const clusterZoom = Math.min(Math.floor(zoomLevel), 17);
      let clustersResult = activeCluster.getClusters(bbox, clusterZoom) as MapFeature[];

      // If we have too many individual points (not clusters), limit them
      const individualPoints = clustersResult.filter(f => !isCluster(f));
      if (individualPoints.length > MAX_MARKERS) {
        // Keep all clusters, but limit individual points
        const clusters = clustersResult.filter(f => isCluster(f));
        const limitedPoints = individualPoints.slice(0, MAX_MARKERS);
        clustersResult = [...clusters, ...limitedPoints];

        console.log(`[useSupercluster] ⚠️ Limiting markers: ${individualPoints.length} → ${limitedPoints.length} individual points`);
      }

      // Overall limit as safety net
      if (clustersResult.length > MAX_MARKERS * 1.5) {
        // Prioritize clusters over individual points
        const clusters = clustersResult.filter(f => isCluster(f));
        const points = clustersResult.filter(f => !isCluster(f));
        const remainingSlots = Math.max(0, MAX_MARKERS * 1.5 - clusters.length);
        clustersResult = [...clusters, ...points.slice(0, remainingSlots)];

        console.log(`[useSupercluster] ⚠️ Overall limit applied: ${clustersResult.length} markers`);
      }

      return clustersResult;
    } catch (e) {
      console.error("Error getting clusters:", e);
      return [];
    }
  }, [supercluster, filteredSupercluster, mapBounds, zoomLevel, drawnPolygon, filters.searchText]);

  // Get operators in viewport
  const operatorsInView = useMemo(() => {
    const hasFilters = drawnPolygon || filters.searchText;
    const baseOperators = hasFilters ? filteredByPolygon : allOperators;

    if (!mapBounds || baseOperators.length === 0) {
      return [];
    }

    const inBounds = baseOperators.filter((op) =>
      op.lat >= mapBounds.south &&
      op.lat <= mapBounds.north &&
      op.lng >= mapBounds.west &&
      op.lng <= mapBounds.east
    );
    // Collapse to one entry per event so "Visible" counts/lists match the map.
    return aggregateByEvent(inBounds);
  }, [allOperators, mapBounds, drawnPolygon, filters.searchText, filteredByPolygon]);

  // Count points (aggregated events) in viewport
  const totalInView = useMemo(() => {
    return operatorsInView.length;
  }, [operatorsInView]);

  // Get expansion zoom for a cluster
  const getClusterExpansionZoom = useCallback((clusterId: number): number => {
    const hasFilters = drawnPolygon || filters.searchText;
    const activeCluster = hasFilters ? filteredSupercluster : clusterRef.current;
    if (!activeCluster) return zoomLevel + 2;

    try {
      return activeCluster.getClusterExpansionZoom(clusterId);
    } catch {
      return zoomLevel + 2;
    }
  }, [drawnPolygon, filters.searchText, filteredSupercluster, zoomLevel]);

  // Get leaves (individual points) for a cluster
  const getClusterLeaves = useCallback((clusterId: number, limit = 100): OperatorWithCoords[] => {
    const hasFilters = drawnPolygon || filters.searchText;
    const activeCluster = hasFilters ? filteredSupercluster : clusterRef.current;
    if (!activeCluster) return [];

    try {
      const leaves = activeCluster.getLeaves(clusterId, limit);
      return leaves.map((f) => f.properties as OperatorWithCoords);
    } catch {
      return [];
    }
  }, [drawnPolygon, filters.searchText, filteredSupercluster]);

  // Sync totalInView to store
  useEffect(() => {
    setTotalInView(totalInView, false);
  }, [totalInView, setTotalInView]);

  // Sync filteredCount to store (always the full filtered count, independent of listMode)
  useEffect(() => {
    const hasFilters = drawnPolygon || filters.searchText;
    const count = hasFilters ? filteredOperators.length : aggregatedAll.length;
    setFilteredCount(count);
  }, [aggregatedAll, filteredOperators, drawnPolygon, filters.searchText, setFilteredCount]);

  // Sync results to store based on listMode
  // "filtered" = all operators with filters applied
  // "visible" = only operators in current map viewport
  useEffect(() => {
    const hasFilters = drawnPolygon || filters.searchText;
    const baseOperators = hasFilters ? filteredOperators : aggregatedAll;

    if (listMode === "visible") {
      // Show only operators in current viewport (already aggregated per event)
      setResults(operatorsInView, operatorsInView.length);
    } else {
      // Show all operators (filtered), aggregated per event
      setResults(baseOperators, baseOperators.length);
    }
  }, [aggregatedAll, drawnPolygon, filters.searchText, filteredOperators, operatorsInView, listMode, setResults]);

  // Expose all lists aggregated per event so every consumer (map, counts,
  // analytics, category legend) is consistent with one-pin-per-event.
  const aggregatedUnfiltered = useMemo(() => aggregateByEvent(unfilteredOperators), [unfilteredOperators]);
  const aggregatedForCounting = useMemo(() => aggregateByEvent(operatorsForCounting), [operatorsForCounting]);

  return {
    clusters,
    allOperators: aggregatedAll,
    filteredOperators,
    operatorsInView,
    totalCount: aggregatedAll.length,
    totalInView,
    isLoading,
    isError,
    getClusterExpansionZoom,
    getClusterLeaves,
    hasPolygon: !!drawnPolygon,
    unfilteredOperators: aggregatedUnfiltered, // Export unfiltered operators for counting
    operatorsForCounting: aggregatedForCounting, // Operators filtered by geographic filters only (for category counts)
  };
}

/**
 * Ray-casting algorithm for point-in-polygon test
 */
function isPointInPolygon(point: [number, number], polygon: GeoJSON.Polygon): boolean {
  const [x, y] = point;
  const coords = polygon.coordinates[0];
  let inside = false;

  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i][0];
    const yi = coords[i][1];
    const xj = coords[j][0];
    const yj = coords[j][1];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}
